import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaCloudUploadAlt, FaCheckCircle } from 'react-icons/fa';
import { uploadEventPhotos } from '../services/api';

const EventPhotos = () => {
  const { eventId } = useParams();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate files
    const validFiles = selectedFiles.filter(file => {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return false;
      }
      if (file.size > 16 * 1024 * 1024) {
        setError('Each file must be less than 16MB');
        return false;
      }
      return true;
    });

    setFiles(validFiles);
    setError('');
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one photo');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('photos[]', file);
    });

    try {
      const response = await uploadEventPhotos(eventId, formData);
      const totalFaces = response.photos.reduce((sum, photo) => sum + photo.faces_detected, 0);

      setSuccess(
        `Successfully uploaded ${response.photos.length} photos! ` +
        `Detected ${totalFaces} faces and matched with users.`
      );
      setFiles([]);
      setUploadProgress(100);

      setTimeout(() => {
        navigate(`/event/${eventId}/browse`);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  return (
    <div className="container">
      <div className="upload-container" style={{ maxWidth: '800px' }}>
        <h2 style={{ textAlign: 'center', color: 'var(--primary-orange)', marginBottom: '1rem' }}>
          Upload Event Photos
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--gray)', marginBottom: '2rem' }}>
          Upload photos from your event. Our AI will automatically detect faces and match them with registered users.
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div 
          className="upload-zone"
          onClick={() => document.getElementById('multiFileInput').click()}
        >
          <div className="upload-icon">
            <FaCloudUploadAlt />
          </div>
          <h3>Click to Select Photos</h3>
          <p style={{ marginTop: '0.5rem' }}>You can select multiple photos at once</p>
          <p style={{ fontSize: '0.9rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
            Supported formats: JPG, PNG (Max 16MB per file)
          </p>
        </div>

        <input
          id="multiFileInput"
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {files.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>
              Selected Photos ({files.length})
            </h3>
            
            <div style={{ 
              maxHeight: '300px', 
              overflowY: 'auto', 
              background: 'var(--off-white)', 
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              {files.map((file, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.8rem',
                    background: 'var(--white)',
                    marginBottom: '0.5rem',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px var(--shadow)'
                  }}
                >
                  <div>
                    <strong>{file.name}</strong>
                    <span style={{ color: 'var(--gray)', marginLeft: '1rem' }}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    style={{
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={handleUpload}
              className="btn-submit"
              disabled={uploading}
            >
              {uploading 
                ? `Uploading and Processing... ${uploadProgress}%` 
                : `Upload ${files.length} Photo${files.length > 1 ? 's' : ''}`}
            </button>
          </div>
        )}

        <div style={{ 
          marginTop: '2rem', 
          padding: '1.5rem', 
          background: 'var(--off-white)', 
          borderRadius: '12px',
          border: '2px solid var(--light-green)'
        }}>
          <h4 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>
            <FaCheckCircle /> What Happens Next?
          </h4>
          <ul style={{ color: 'var(--gray)', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
            <li>Photos are uploaded to the event</li>
            <li>AI automatically detects all faces in each photo</li>
            <li>Faces are matched against registered user profiles</li>
            <li>Users are notified of their matched photos</li>
            <li>Confidence scores are calculated for each match</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default EventPhotos;
